import type { ReactNode } from "react";
import { css, cx } from "styled-system/css";

export interface LedgerItem {
  label: string;
  current?: number | null;
  /** Preformatted current value; wins over `current`. */
  value?: ReactNode;
  /** Prior value, a number to format or preformatted text. */
  prior?: number | string | null;
  format?: (v: number | null) => string;
  /** Short change text, e.g. "+18%" (computed by the caller). */
  change?: string;
  arrow?: string;
  note?: ReactNode;
  /** Faint tint on the change (or on the note when there's no change). */
  flag?: "caution" | "emph";
  /** Faint tint on the current value itself. */
  valueFlag?: "caution";
}

const defaultFormat = (v: number | null) =>
  v === null || !Number.isFinite(v) ? "–" : Math.round(v).toLocaleString();

/**
 * Before and after, side by side: the prior value struck through, then the
 * current one. After the reference's ledger grid.
 *
 * `hideMissingPrior` drops the struck prior and its arrow when an item has
 * no prior value, so a stat with no comparison doesn't show a struck dash.
 * `columns` sets the widest row: 6 (default), 4, or "fit" to match the item
 * count between 4 and 6.
 */
export function Ledger({
  items,
  hideMissingPrior = false,
  columns = 6,
}: {
  items: LedgerItem[];
  hideMissingPrior?: boolean;
  columns?: 4 | 6 | "fit";
}) {
  const cols = columns === "fit" ? `fit-${Math.min(items.length, 6)}` : String(columns);
  return (
    <dl className={styles.grid} data-cols={cols}>
      {items.map((item) => {
        const fmt = item.format ?? defaultFormat;
        const show = (v: number | string | null | undefined) =>
          typeof v === "string" ? v : fmt(v ?? null);
        const hasPrior = !(hideMissingPrior && item.prior == null);
        const current = item.value !== undefined ? item.value : fmt(item.current ?? null);
        return (
          <div key={item.label} className={styles.cell}>
            <dt className={styles.label}>{item.label}</dt>
            <dd className={styles.vals}>
              {hasPrior && (
                <>
                  <span className={styles.prior} aria-label={`was ${show(item.prior)}`}>
                    {show(item.prior)}
                  </span>
                  <span className={styles.arrowSep} aria-hidden>
                    →
                  </span>
                </>
              )}
              <span className={styles.current}>
                {item.valueFlag ? <span data-flag={item.valueFlag}>{current}</span> : current}
              </span>
            </dd>
            {(item.change || item.note) && (
              <dd className={styles.note}>
                {item.change && (
                  <span className={cx(styles.change, styles.flaggable)} data-flag={item.flag}>
                    {item.arrow ? `${item.arrow} ` : ""}
                    {item.change}
                  </span>
                )}
                {item.note && (
                  <span
                    className={styles.flaggable}
                    data-flag={!item.change ? item.flag : undefined}
                  >
                    {item.change ? " " : ""}
                    {item.note}
                  </span>
                )}
              </dd>
            )}
          </div>
        );
      })}
    </dl>
  );
}

const styles = {
  grid: css({
    display: "grid",
    gridTemplateColumns: {
      base: "repeat(2, minmax(0, 1fr))",
      md: "repeat(3, minmax(0, 1fr))",
    },
    "&[data-cols='6'], &[data-cols='fit-6']": {
      gridTemplateColumns: { xl: "repeat(6, minmax(0, 1fr))" },
    },
    "&[data-cols='fit-5']": {
      gridTemplateColumns: { xl: "repeat(5, minmax(0, 1fr))" },
    },
    "&[data-cols='fit-4']": {
      gridTemplateColumns: { xl: "repeat(4, minmax(0, 1fr))" },
    },
    "&[data-cols='4']": {
      gridTemplateColumns: { md: "repeat(4, minmax(0, 1fr))" },
    },
    borderTopWidth: "2px",
    borderTopStyle: "solid",
    borderColor: "ed.ink",
    m: 0,
  }),
  cell: css({
    px: "12px",
    pt: "10px",
    pb: "12px",
    borderRightWidth: "1px",
    borderRightStyle: "solid",
    borderBottomWidth: "1px",
    borderBottomStyle: "solid",
    borderColor: "ed.rule",
    minWidth: 0,
  }),
  label: css({
    fontFamily: "sans",
    fontSize: "10.5px",
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "ed.ink2",
    mb: "6px",
  }),
  vals: css({
    m: 0,
    display: "flex",
    alignItems: "baseline",
    gap: "6px",
    flexWrap: "wrap",
    fontFamily: "mono",
    fontVariantNumeric: "tabular-nums",
  }),
  prior: css({
    fontSize: "15px",
    color: "ed.ink3",
    textDecoration: "line-through",
    textDecorationThickness: "1.5px",
    textDecorationColor: "ed.ink3",
  }),
  arrowSep: css({ color: "ed.ink3", fontSize: "13px" }),
  current: css({
    fontSize: "26px",
    fontWeight: 600,
    color: "ed.ink",
    letterSpacing: "-0.02em",
    lineHeight: 1.1,
    "& [data-flag=caution]": { bg: "ed.cautionTint", px: "2px" },
  }),
  note: css({
    m: 0,
    mt: "4px",
    fontFamily: "mono",
    fontSize: "11px",
    color: "ed.ink3",
    lineHeight: 1.35,
    fontVariantNumeric: "tabular-nums",
  }),
  /** Padded whether or not it's tinted, so flagged and plain changes line up. */
  change: css({ color: "ed.ink", px: "2px" }),
  /** A note brings padding only with its tint. */
  flaggable: css({
    "&[data-flag=caution]": { bg: "ed.cautionTint", px: "2px", color: "ed.ink" },
    "&[data-flag=emph]": { bg: "ed.emphTint", px: "2px", color: "ed.ink" },
  }),
};
